import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  DocumentTextIcon, AcademicCapIcon, BuildingLibraryIcon,
  UserGroupIcon, EnvelopeIcon, EyeIcon, ArrowTrendingUpIcon,
  ClockIcon, ChartBarIcon, PlusIcon
} from '@heroicons/react/24/outline';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Stats, Card, Loading } from '../../components/Common';
import api from '../../services/api';

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchAnalyticsData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching dashboard data...');
      const response = await api.get('/admin/dashboard');
      console.log('Dashboard response:', response.data);

      if (response.data.success && response.data.data) {
        setDashboardData(response.data.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load dashboard data: ' + (err.response?.data?.message || err.message));
      // Set default data on error
      setDashboardData({
        overview: { totalArticles: 0, totalCourses: 0, totalUniversities: 0, totalUsers: 0, totalContacts: 0, totalViews: 0 },
        quickStats: { articleGrowth: 0, userGrowth: 0 },
        recentActivity: []
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      console.log('Fetching analytics data...');
      const response = await api.get('/admin/analytics/dashboard');
      console.log('Analytics response:', response.data);

      if (response.data.success && response.data.data) {
        setAnalyticsData(response.data.data);
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
      // Analytics is optional, don't show error
    }
  };

  const userName = user?.firstName || user?.email?.split('@')[0] || 'Admin';

  // Chart data - use real data from analytics API or fallback to defaults
  const contentData = analyticsData?.monthlyStats?.map(stat => ({
    name: stat.month,
    articles: stat.articleCount || 0
  })) || [
      { name: 'Jan', articles: 12 },
      { name: 'Feb', articles: 19 },
      { name: 'Mar', articles: 15 },
      { name: 'Apr', articles: 25 },
      { name: 'May', articles: 22 },
      { name: 'Jun', articles: 30 },
    ];

  // Category data from analytics
  const categoryColors = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6'];
  const categoryData = analyticsData?.categoryStats?.slice(0, 8).map((cat, index) => ({
    name: cat.name,
    value: cat.articleCount || 0,
    color: categoryColors[index % categoryColors.length]
  })) || [
      { name: 'Technology', value: 35, color: '#6366f1' },
      { name: 'Education', value: 25, color: '#8b5cf6' },
      { name: 'Career', value: 20, color: '#06b6d4' },
      { name: 'Lifestyle', value: 12, color: '#10b981' },
      { name: 'Others', value: 8, color: '#f59e0b' },
    ];

  // Traffic data - use monthly stats (article views per month)
  const trafficData = analyticsData?.monthlyStats?.slice(-7).map((stat, index) => ({
    name: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][index] || stat.month,
    views: stat.viewCount || stat.articleCount || 0
  })) || [
      { name: 'Mon', views: 0 },
      { name: 'Tue', views: 0 },
      { name: 'Wed', views: 0 },
      { name: 'Thu', views: 0 },
      { name: 'Fri', views: 0 },
      { name: 'Sat', views: 0 },
      { name: 'Sun', views: 0 },
    ];

  if (loading) return <Loading fullScreen text="Loading dashboard..." />;

  const overview = dashboardData?.overview || {};
  const quickStats = dashboardData?.quickStats || {};
  const recentActivity = dashboardData?.recentActivity || [];

  // Show error if any
  if (error) {
    console.warn('Dashboard error state:', error);
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Welcome Header */}
      <div className="bg-gradient-primary rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="relative z-10">
          <h1 className="text-2xl font-bold mb-2">Welcome back, {userName}! 👋</h1>
          <p className="text-white/80">Here's what's happening with your platform today.</p>
        </div>
        <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden lg:block">
          <div className="w-32 h-32 bg-white/10 rounded-full"></div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stats
          title="Total Articles"
          value={overview.totalArticles || 0}
          change={quickStats.articleGrowth}
          changeType={quickStats.articleGrowth >= 0 ? 'increase' : 'decrease'}
          icon={DocumentTextIcon}
          iconBg="bg-indigo-100"
          iconColor="text-indigo-600"
        />
        <Stats
          title="Total Courses"
          value={overview.totalCourses || 0}
          icon={AcademicCapIcon}
          iconBg="bg-cyan-100"
          iconColor="text-cyan-600"
        />
        <Stats
          title="Universities"
          value={overview.totalUniversities || 0}
          icon={BuildingLibraryIcon}
          iconBg="bg-emerald-100"
          iconColor="text-emerald-600"
        />
        <Stats
          title="Categories"
          value={overview.totalCategories || 0}
          icon={ChartBarIcon}
          iconBg="bg-purple-100"
          iconColor="text-purple-600"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Stats
          title="Total Users"
          value={overview.totalUsers || 0}
          change={quickStats.userGrowth}
          changeType={quickStats.userGrowth >= 0 ? 'increase' : 'decrease'}
          icon={UserGroupIcon}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
        />
        <Stats
          title="Admin Users"
          value={overview.totalAdmins || 0}
          icon={UserGroupIcon}
          iconBg="bg-purple-100"
          iconColor="text-purple-600"
        />
        <Stats
          title="Contact Messages"
          value={overview.totalContacts || 0}
          icon={EnvelopeIcon}
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
        />
        <Stats
          title="Total Views"
          value={overview.totalViews || '0'}
          icon={EyeIcon}
          iconBg="bg-rose-100"
          iconColor="text-rose-600"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Content Growth Chart */}
        <Card title="Content Growth" subtitle="Articles over time" icon={ArrowTrendingUpIcon}>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={contentData}>
                <defs>
                  <linearGradient id="colorArticles" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip
                  contentStyle={{ background: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                />
                <Legend />
                <Area type="monotone" dataKey="articles" stroke="#6366f1" fillOpacity={1} fill="url(#colorArticles)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Category Distribution */}
        <Card title="Category Distribution" subtitle="Content by category" icon={ChartBarIcon}>
          <div className="h-72 flex items-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Traffic Chart */}
      <Card title="Article Views" subtitle="Total article views (by month)" icon={EyeIcon}>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trafficData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} />
              <Tooltip
                contentStyle={{ background: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
              />
              <Bar dataKey="views" fill="#6366f1" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card title="Recent Activity" subtitle="Latest updates" className="lg:col-span-2" noPadding>
          <div className="divide-y divide-slate-100">
            {recentActivity.length > 0 ? (
              recentActivity.slice(0, 5).map((activity, index) => (
                <div key={activity.id || index} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${activity.type === 'article' ? 'bg-indigo-100 text-indigo-600' :
                    activity.type === 'user' ? 'bg-blue-100 text-blue-600' :
                      'bg-amber-100 text-amber-600'
                    }`}>
                    {activity.type === 'article' ? <DocumentTextIcon className="w-5 h-5" /> :
                      activity.type === 'user' ? <UserGroupIcon className="w-5 h-5" /> :
                        <EnvelopeIcon className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">{activity.title}</p>
                    <p className="text-sm text-slate-500 truncate">{activity.description}</p>
                  </div>
                  <div className="text-xs text-slate-400 flex items-center gap-1">
                    <ClockIcon className="w-4 h-4" />
                    {activity.timestamp ? new Date(activity.timestamp).toLocaleDateString() : 'Recently'}
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-slate-500">No recent activity</div>
            )}
          </div>
        </Card>

        {/* Quick Actions */}
        <Card title="Quick Actions" subtitle="Common tasks">
          <div className="space-y-3">
            <Link to="/articles/new" className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-indigo-50 hover:text-indigo-600 transition-colors group">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                <PlusIcon className="w-5 h-5 text-indigo-600" />
              </div>
              <span className="font-medium">New Article</span>
            </Link>
            <Link to="/courses/new" className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-cyan-50 hover:text-cyan-600 transition-colors group">
              <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                <PlusIcon className="w-5 h-5 text-cyan-600" />
              </div>
              <span className="font-medium">Add Course</span>
            </Link>
            <Link to="/universities/new" className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-colors group">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                <PlusIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="font-medium">Add University</span>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
